/**
 * Minimaler Toast-Helper als Ersatz für `window.alert()`. Stack rechts oben,
 * 5s Auto-Dismiss (länger bei Errors), schließbar per ✕. Mehrere Toasts stacken
 * untereinander. Vario-Slot rendert die Frontend-App in einem iframe - `alert()`
 * würde das gesamte Vario-UI blockieren und sieht unprofessionell aus.
 *
 * Drei Level: `info` (default, neutral), `success` (grün), `error` (rot,
 * längere Dauer da der User oft Details brauchen wird).
 */

export type ToastLevel = 'info' | 'success' | 'error';

const STACK_ID = 'kl-toast-stack';
const DEFAULT_DURATION_MS: Record<ToastLevel, number> = {
  info: 5_000,
  success: 5_000,
  error: 10_000,
};

function ensureStack(): HTMLDivElement {
  let stack = document.getElementById(STACK_ID) as HTMLDivElement | null;
  if (!stack) {
    stack = document.createElement('div');
    stack.id = STACK_ID;
    stack.className = 'kl-toast-stack';
    document.body.appendChild(stack);
  }
  return stack;
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) =>
    c === '&' ? '&amp;' : c === '<' ? '&lt;' : c === '>' ? '&gt;' : c === '"' ? '&quot;' : '&#39;',
  );
}

export function showToast(message: string, level: ToastLevel = 'info'): void {
  const stack = ensureStack();
  const toast = document.createElement('div');
  toast.className = `kl-toast kl-toast--${level}`;
  toast.innerHTML = `
    <span class="kl-toast-msg">${escapeHtml(message)}</span>
    <button type="button" class="kl-toast-close" aria-label="Schließen">&times;</button>
  `;
  stack.appendChild(toast);

  const dismiss = () => {
    toast.classList.add('kl-toast--leaving');
    setTimeout(() => toast.remove(), 200);
  };
  toast.querySelector<HTMLButtonElement>('.kl-toast-close')?.addEventListener('click', dismiss);
  setTimeout(dismiss, DEFAULT_DURATION_MS[level]);
}

/** Griff auf einen laufenden Fortschritts-Hinweis. */
export interface ProgressToast {
  /** Text austauschen, während die Arbeit läuft. */
  update(message: string): void;
  /** Abschließen: wird zu einem normalen Toast und verschwindet von selbst. */
  finish(message: string, level?: ToastLevel): void;
  /** Ersatzlos entfernen (wenn der Aufrufer das Ergebnis selbst meldet). */
  close(): void;
}

/**
 * Bleibender Hinweis mit Spinner für Sammelaktionen, die länger dauern.
 *
 * **Warum es das braucht:** `showToast` verschwindet nach fünf Sekunden. Eine Sammelaktion über
 * hunderte Artikel läuft aber Minuten — der Anwender sah nach dem Start nichts mehr und wusste
 * nicht, ob überhaupt etwas passiert (Betreiber 27.08.2026, Anlage von 273 Artikeln). Dieser Toast
 * bleibt stehen und lässt sich fortschreiben.
 *
 * Kein Auto-Dismiss und kein Schließen-Kreuz: solange er steht, läuft die Arbeit. Der Aufrufer
 * beendet ihn mit `finish` oder `close`.
 */
export function showProgressToast(message: string): ProgressToast {
  const stack = ensureStack();
  const toast = document.createElement('div');
  toast.className = 'kl-toast kl-toast--info';
  toast.innerHTML = `
    <span class="v-icon fa-regular fa-spinner-third fa-spin" aria-hidden="true"></span>
    <span class="kl-toast-msg">${escapeHtml(message)}</span>
  `;
  stack.appendChild(toast);
  const msgEl = toast.querySelector<HTMLElement>('.kl-toast-msg')!;

  const remove = () => {
    toast.classList.add('kl-toast--leaving');
    setTimeout(() => toast.remove(), 200);
  };

  return {
    update(text: string): void { msgEl.textContent = text; },
    finish(text: string, level: ToastLevel = 'success'): void {
      toast.className = `kl-toast kl-toast--${level}`;
      toast.innerHTML = `
        <span class="kl-toast-msg">${escapeHtml(text)}</span>
        <button type="button" class="kl-toast-close" aria-label="Schließen">&times;</button>
      `;
      toast.querySelector<HTMLButtonElement>('.kl-toast-close')?.addEventListener('click', remove);
      setTimeout(remove, DEFAULT_DURATION_MS[level]);
    },
    close: remove,
  };
}
